import React from 'react';
import { Upload } from 'antd';
import { UploadOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import '../css/CustomUploadField.css';
import "../css/label.css";
import "../css/customErrorForFields.css";
import Label from "./Label";

/**
 * CustomUploadField Component
 * Enhanced with preview functionality for documents
 */
export default function CustomUploadField({
  mdValue,
  smValue,
  xsValue,
  lgValue,
  controlId,
  labelText,
  fileList = [],
  handleFileChange,
  errorMessage,
  isRequired,
  isCompulsary,
  accept = ".pdf,.png,.jpeg,.jpg",
  maxCount = 1,
  wrapperClass,
  onPreview,
  disabled = false,
  multiple = false,
  disableUploader=false,
  ...props
}) {
  const hasFile = fileList && fileList.length > 0;
  const uploadedFile = hasFile ? fileList[0] : null;
  const hasError = Boolean(errorMessage);

  const handleRemove = (e) => {
    e.stopPropagation();
    if (handleFileChange && !disabled) {
      handleFileChange({ fileList: [] });
    }
  };

  const handleRemoveFile = (fileToRemove, e) => {
    e.stopPropagation();
    if (handleFileChange && !disabled) {
      const updatedList = fileList.filter(file => file.uid !== fileToRemove.uid);
      handleFileChange({ fileList: updatedList });
    }
  };

  const handlePreviewFile = (file, e) => {
    e.stopPropagation();
    if (onPreview && file) {
      onPreview(file);
    } else {
      defaultPreview(file);
    }
  };

  /**
   * Handle preview click
   */
  const handlePreviewClick = (e) => {
    e.stopPropagation();
    if (onPreview && uploadedFile) {
      onPreview(uploadedFile);
    } else {
      // Fallback preview logic if onPreview is not provided
      defaultPreview(uploadedFile);
    }
  };

  /**
   * Default preview logic if custom onPreview is not provided
   */
  const defaultPreview = (file) => {
    if (!file) return;
    const fileUrl = file.url || file.preview || file.thumbUrl;
    if (!fileUrl) {
      console.warn('No preview URL available for file:', file);
      return;
    }

    // Determine file type
    const isImage = file.type?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name);
    const isPDF = file.type === 'application/pdf' || file.name?.endsWith('.pdf');

    // Open preview in new window
    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      console.error('Failed to open preview window. Please allow popups.');
      return;
    }

    if (isImage) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Preview - ${file.name}</title>
            <style>
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                background: #000;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
              }
            </style>
          </head>
          <body>
            <img src="${fileUrl}" alt="${file.name}" />
          </body>
        </html>
      `);
    } else if (isPDF) {
      const base64Data = fileUrl.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
      newWindow.document.write(`
        <html>
          <head>
            <title>Preview - ${file.name}</title>
            <style>
              body { margin: 0; }
              iframe { width: 100%; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileUrl}" type="application/pdf"></iframe>
          </body>
        </html>
      `);
    } else {
      // For other file types, trigger download
      newWindow.close();
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = file.name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /**
   * Check if file can be previewed
   */
  const canPreview = (file) => {
    if (!file) return false;
    const hasUrl = Boolean(file.url || file.preview || file.thumbUrl);
    return hasUrl;
  };

 if (multiple) {
    return (
      <div className="custom-field-wrapper">
        {labelText && (
          <Label
            className={"custom-label"}
            labelText={labelText}
            isCompulsary={isCompulsary}
          />
        )}

        <div className="upload-container-multiple">
          {/* Upload Button */}
          {!disableUploader && 
          <Upload
            id={controlId}
            className={`media-upload-wrapper ${disabled ? 'disabled' : ''}`}
            onChange={handleFileChange}
            accept={accept}
            maxCount={maxCount}
            multiple={true}
            showUploadList={false}
            isRequired={isRequired}
            beforeUpload={() => false}
            fileList={fileList}
            disabled={disabled}
            {...props}
          >
            <div className={`media-upload-trigger ${disabled ? 'disabled' : ''}`}>
              Upload File <UploadOutlined />
            </div>
          </Upload>
          }

          {/* File Chips */}
          {hasFile && (
            <div className="file-chips-container">
              {fileList.map((file) => (
                <div key={file.uid} className="file-chip">
                  <span className="file-chip-name" title={file.name}>
                    {file.name}
                  </span>
                  <div className="file-chip-actions">
                    {canPreview(file) && (
                      <EyeOutlined
                        className="custom-preview-icon"
                        onClick={(e) => handlePreviewFile(file, e)}
                        title="Preview"
                      />
                    )}
                    {!disabled && (
                      <CloseCircleOutlined
                        className="chip-icon remove-icon"
                        onClick={(e) => handleRemoveFile(file, e)}
                        title="Remove"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {hasError && (
          <div className="upload-error-text">
            {errorMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="custom-field-wrapper">
      {/* Label */}
      {labelText && (
        <Label
          className={"custom-label"}
          labelText={labelText}
          isCompulsary={isCompulsary}
        />
      )}

      {/* Upload Component */}
      <Upload
        id={controlId}
        className={`media-upload-wrapper ${disabled ? 'disabled' : ''}`}
        onChange={handleFileChange}
        accept={accept}
        maxCount={maxCount}
        showUploadList={false}
        isRequired={isRequired}
        beforeUpload={() => false}
        fileList={fileList}
        disabled={disabled}
        {...props}
      >
        {hasFile ? (
          <div className="custom-file-item">
            <span className="custom-file-name" title={uploadedFile.name}>
              {uploadedFile.name}
            </span>
            <div className="custom-file-actions">
              {/* Preview Icon */}
              {canPreview(uploadedFile) && (
                <EyeOutlined
                  className="custom-preview-icon"
                  onClick={handlePreviewClick}
                  title="Preview document"
                />
              )}
              {/* Remove Icon */}
              {!disabled && (
                <CloseCircleOutlined
                  className="custom-remove-icon"
                  onClick={handleRemove}
                  title="Remove file"
                />
              )}
            </div>
          </div>
        ) : (
          <div className={`media-upload-trigger ${disabled ? 'disabled' : ''}`}>
            Upload File <UploadOutlined />
          </div>
        )}
      </Upload>

      {/* Error Message */}
      {hasError && (
        <div className="upload-error-text">
          {errorMessage}
        </div>
      )}
    </div>
  );
}